import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';
import { sankey as d3Sankey, sankeyLinkHorizontal } from 'd3-sankey';
import {
    Users, Brain, Target, Eye, MessageSquare, TrendingUp, 
    MapPin, Clock, FileText, ChevronDown, 
    Home, Briefcase, Heart, Shield, Zap, Book, Activity,
    User as UserIcon, Crosshair, Sparkles, PieChart,
    Compass, TrendingDown, LifeBuoy, Percent, X, PoundSterling, Vote, AlertCircle,
    BarChart2, Megaphone, Layers, Scale, Anchor, Lightbulb, Check
} from 'lucide-react';

// Import data - Ensure this path is correct for your project structure
import archetypeSummaryData from '../data/archetype_summary.json';

// --- STYLES ---
const globalStyles = `
  .scrollbar-hide::-webkit-scrollbar {
      display: none;
  }
  .scrollbar-hide {
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;  /* Firefox */
  }
  .glass-panel {
      background: rgba(30, 41, 59, 0.4);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.1);
  }
  .sankey-node:hover {
      filter: brightness(1.2);
  }
  .matrix-dot {
      transition: all 0.3s ease;
  }
  .matrix-dot:hover {
      stroke: white;
      stroke-width: 2px;
      filter: drop-shadow(0 0 4px rgba(255,255,255,0.5));
      opacity: 1;
  }
`;

// --- CONSTANTS ---
const PARTY_COLORS = {
  "Labour": "#E4003B",
  "Conservative": "#0087DC",
  "Liberal Democrats": "#FAA61A",
  "Green": "#6AB023",
  "Reform UK": "#12B6CF", // Distinct Teal for Reform
  "Other/Abstain": "#64748b"
};

// BASE PROPENSITY MAPPING (Calibrated V.5.0.0 'Shattered Britain' Update)
const ARCHETYPE_PROPENSITIES = {
  "URBAN_PROGRESSIVE_CORE": { "Labour": 0.55, "Green": 0.25, "Liberal Democrats": 0.18, "Conservative": 0.02, "Reform UK": 0.00, "Other/Abstain": 0.00 },
  "CLIMATE_ACTIVIST": { "Green": 0.65, "Labour": 0.25, "Liberal Democrats": 0.09, "Conservative": 0.01, "Reform UK": 0.00, "Other/Abstain": 0.00 },
  "SOCIAL_JUSTICE_WARRIOR": { "Labour": 0.55, "Green": 0.35, "Liberal Democrats": 0.08, "Conservative": 0.01, "Reform UK": 0.00, "Other/Abstain": 0.01 },
  "SOFT_LEFT_LIBERAL": { "Labour": 0.40, "Liberal Democrats": 0.35, "Green": 0.15, "Conservative": 0.08, "Reform UK": 0.02, "Other/Abstain": 0.00 },
  "CIVIC_PRAGMATIST": { "Labour": 0.35, "Liberal Democrats": 0.35, "Conservative": 0.15, "Green": 0.12, "Reform UK": 0.03, "Other/Abstain": 0.00 },
  "MIDDLE_BRITAIN_LEFT": { "Labour": 0.40, "Conservative": 0.25, "Liberal Democrats": 0.20, "Reform UK": 0.10, "Green": 0.05, "Other/Abstain": 0.00 },
  "PROFESSIONAL_MODERATE": { "Liberal Democrats": 0.42, "Labour": 0.30, "Conservative": 0.22, "Green": 0.05, "Reform UK": 0.01, "Other/Abstain": 0.00 },
  "WELL_OFF_TRADITIONALIST": { "Liberal Democrats": 0.45, "Conservative": 0.35, "Labour": 0.10, "Reform UK": 0.07, "Green": 0.03, "Other/Abstain": 0.00 },
  "ESTABLISHED_LIBERAL_CORE": { "Liberal Democrats": 0.55, "Conservative": 0.25, "Labour": 0.15, "Green": 0.04, "Reform UK": 0.01, "Other/Abstain": 0.00 },
  "OPTIMISTIC_CONTENTMENT": { "Conservative": 0.45, "Liberal Democrats": 0.35, "Labour": 0.15, "Green": 0.03, "Reform UK": 0.02, "Other/Abstain": 0.00 },
  "APOLITICAL_CENTRIST": { "Reform UK": 0.35, "Labour": 0.15, "Green": 0.12, "Conservative": 0.10, "Liberal Democrats": 0.08, "Other/Abstain": 0.20 },
  "PASSIVE_CENTRE": { "Reform UK": 0.25, "Labour": 0.20, "Conservative": 0.15, "Liberal Democrats": 0.08, "Green": 0.06, "Other/Abstain": 0.26 },
  "CALM_PERSISTENCE": { "Labour": 0.25, "Reform UK": 0.15, "Green": 0.15, "Conservative": 0.10, "Liberal Democrats": 0.10, "Other/Abstain": 0.25 },
  "LEFT_BEHIND_PATRIOT": { "Reform UK": 0.65, "Labour": 0.18, "Conservative": 0.15, "Liberal Democrats": 0.01, "Green": 0.01, "Other/Abstain": 0.00 },
  "LOYAL_NATIONAL": { "Reform UK": 0.45, "Conservative": 0.35, "Labour": 0.15, "Liberal Democrats": 0.03, "Green": 0.01, "Other/Abstain": 0.01 },
  "BACKBONE_CONSERVATIVE_PATRIOT": { "Conservative": 0.45, "Reform UK": 0.45, "Labour": 0.08, "Liberal Democrats": 0.015, "Green": 0.003, "Other/Abstain": 0.002 },
  "DISENGAGED_TRADITIONALIST_PATRIOT": { "Reform UK": 0.40, "Conservative": 0.35, "Labour": 0.15, "Liberal Democrats": 0.05, "Green": 0.03, "Other/Abstain": 0.02 },
  "WELL_OFF_TRADITIONALIST_CONSERVATIVE": { "Conservative": 0.75, "Reform UK": 0.15, "Liberal Democrats": 0.08, "Labour": 0.02, "Green": 0.00, "Other/Abstain": 0.00 },
  "BACKBONE_CONSERVATIVE_CORE": { "Conservative": 0.82, "Reform UK": 0.15, "Liberal Democrats": 0.02, "Labour": 0.01, "Green": 0.00, "Other/Abstain": 0.00 },
  "DISENGAGED_TRADITIONALIST_CONSERVATIVE": { "Conservative": 0.60, "Reform UK": 0.25, "Liberal Democrats": 0.08, "Labour": 0.05, "Green": 0.02, "Other/Abstain": 0.00 },
  "DISSENTING_DISRUPTOR_CORE": { "Reform UK": 0.85, "Conservative": 0.08, "Labour": 0.05, "Liberal Democrats": 0.01, "Green": 0.01, "Other/Abstain": 0.00 },
  "LONG_TERM_DESPAIR": { "Reform UK": 0.55, "Labour": 0.15, "Conservative": 0.08, "Liberal Democrats": 0.01, "Green": 0.01, "Other/Abstain": 0.20 },
  "HARD_PRESSED_ANXIETY": { "Reform UK": 0.50, "Labour": 0.25, "Conservative": 0.08, "Liberal Democrats": 0.02, "Green": 0.02, "Other/Abstain": 0.13 }
};

// --- NARRATIVE THEMES ---
const narrativeThemes = {
  challenges: {
    title: "The challenges we face",
    core_message: "After years of Conservative chaos and neglect, families are struggling with sky-high costs while public services fail",
    key_points: ["Conservative chaos and economic crisis", "NHS crisis - people waiting hours for ambulances", "Cost of living crushing families and pensioners", "Public services failing when people need them most", "Political system broken and out of touch"]
  },
  vision: {
    title: "A brighter future", 
    core_message: "Things can get better. Britain has overcome big challenges before and we can do it again",
    key_points: ["Fair, free and open society for everyone", "Liberal Democrats leading change for 150+ years", "Everyone deserves the chance to get on in life", "Hard work and aspiration properly rewarded", "Building on Britain's great strengths and values"]
  },
  solutions: {
    title: "The Liberal Democrat response",
    core_message: "As the biggest liberal party in Parliament in over 100 years, we'll lead the way with practical solutions",
    key_points: ["Responsible opposition holding Labour to account", "Transform our broken political system", "Restore trust and put power in people's hands", "Back businesses to create growth and jobs", "Tackle the climate and nature crises"]
  }
};


// --- HELPER COMPONENTS ---

const Panel = ({ title, icon, controls, children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={`glass-panel rounded-lg overflow-hidden ${className}`}
  >
    {title && (
      <div className="flex items-center justify-between px-6 pt-6 mb-4">
        <h3 className="flex items-center text-lg font-semibold text-white">
          {icon}
          <span className="ml-2">{title}</span>
        </h3>
        {controls && <div className="flex-shrink-0">{controls}</div>}
      </div>
    )}
    <div className={title ? "px-6 pb-6" : "p-6"}>{children}</div>
  </motion.div>
);

const ThreatLevelBadge = ({ level, type = "TURNOUT" }) => {
  const configs = {
    HIGH: { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: TrendingUp },
    MEDIUM: { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: Activity },
    LOW: { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: TrendingDown }
  };
  const config = configs[level] || configs.MEDIUM;
  const Icon = config.icon;
  return (
    <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon size={12} />
      <span>{level} {type}</span>
    </div>
  );
};

const ProgressBar = ({ value, colorClass = "bg-blue-500", label, subLabel }) => (
    <div className="space-y-1">
        <div className="flex justify-between items-end">
            <span className="text-xs font-medium text-slate-300">{label}</span>
            <span className="text-xs text-white font-bold">{subLabel || `${(value * 100).toFixed(1)}%`}</span>
        </div>
        <div className="w-full bg-slate-700/50 rounded-full h-2 overflow-hidden">
            <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(value * 100, 100)}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className={`h-full rounded-full ${colorClass}`}
            />
        </div>
    </div>
);

/// --- D3 HYBRID SANKEY DIAGRAM COMPONENT ---
const SankeyDiagram = ({ archetypes, totalPopulation, predictedVoteShares }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedNodeIndex, setSelectedNodeIndex] = useState(null);
  
  // Track dimensions to ensure flip logic works on resize
  const [containerDim, setContainerDim] = useState({ width: 800, height: 600 });

  // HELPER: Get coordinates relative to the specific container, not the screen
  const getRelativePosition = (event) => {
      if (!containerRef.current) return { x: 0, y: 0 };
      const [x, y] = d3.pointer(event, containerRef.current);
      return { x, y };
  };

  useEffect(() => {
    if (!archetypes || !containerRef.current) return;
    if (archetypes.length === 0) return;
    
    // Update container dimensions for tooltip calculations
    setContainerDim({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight
    });

    // 1. HELPER: Party Key Normalization
    const normalizePartyKey = (dbKey) => {
        const k = dbKey.toLowerCase().trim();
        if (k === 'conservative') return 'Conservative';
        if (k === 'labour') return 'Labour';
        if (k.includes('lib') || k.includes('dem')) return 'Liberal Democrats';
        if (k === 'green') return 'Green';
        if (k === 'reform' || k === 'reform_uk') return 'Reform UK';
        return 'Other/Abstain';
    };

    // 2. PREPARE DATA 
    const nodes = [];
    const links = [];
    const nodeMap = new Map();

    const partyKeys = Object.keys(PARTY_COLORS);
    partyKeys.forEach(party => {
        nodes.push({ name: party, type: 'party', color: PARTY_COLORS[party] });
        nodeMap.set(party, nodes.length - 1);
    });

    archetypes.forEach(arch => {
        const name = arch.name || "Unknown Segment";
        nodes.push({ name: name, type: 'archetype', color: '#64748b' });
        nodeMap.set(name, nodes.length - 1);
    });

    // 3. CALCULATE CALIBRATION
    const calibrationFactors = {};
    if (predictedVoteShares) {
        const theoreticalTotals = {};
        partyKeys.forEach(p => theoreticalTotals[p] = 0);

        archetypes.forEach(arch => {
             const pop = arch.estimated_population || 0;
             const archKey = arch.category || "UNKNOWN";
             const props = ARCHETYPE_PROPENSITIES[archKey] || { "Other/Abstain": 1.0 };
             
             Object.entries(props).forEach(([party, prop]) => {
                 const target = party === "Reform" ? "Reform UK" : party; 
                 if (theoreticalTotals[target] !== undefined) {
                     theoreticalTotals[target] += (pop * prop);
                 }
             });
        });

        partyKeys.forEach(uiParty => {
            let modelPercent = 0;
            const dbKey = Object.keys(predictedVoteShares).find(k => normalizePartyKey(k) === uiParty);
            if (dbKey) modelPercent = predictedVoteShares[dbKey];
            else if (uiParty === 'Other/Abstain') modelPercent = predictedVoteShares.other || 0;

            const modelTotalVoters = (modelPercent / 100) * totalPopulation;
            const theoreticalTotal = theoreticalTotals[uiParty];

            if (modelTotalVoters > 0 && theoreticalTotal === 0) {
                calibrationFactors[uiParty] = { type: 'force', targetTotal: modelTotalVoters };
            } else {
                const ratio = (theoreticalTotal > 0) ? modelTotalVoters / theoreticalTotal : 0;
                calibrationFactors[uiParty] = { type: 'scale', value: ratio };
            }
        });
    }

    // 4. GENERATE LINKS
    archetypes.forEach(arch => {
        const sourceIndex = nodeMap.get(arch.name);
        const archKey = arch.category || "UNKNOWN";
        const baseProps = ARCHETYPE_PROPENSITIES[archKey] || { "Other/Abstain": 1.0 };
        const pop = arch.estimated_population || 0;

        partyKeys.forEach(targetParty => {
            const targetIndex = nodeMap.get(targetParty);
            const calib = calibrationFactors[targetParty];
            
            let finalValue = 0;
            let baseProp = baseProps[targetParty] || 0;

            if (calib && calib.type === 'force') {
                const shareOfPop = pop / totalPopulation;
                finalValue = calib.targetTotal * shareOfPop;
            } else {
                const factor = calib ? calib.value : 1.0;
                finalValue = pop * baseProp * factor;
            }

            if (finalValue > 1) {
                links.push({
                    source: sourceIndex,
                    target: targetIndex,
                    value: Math.round(finalValue),
                    gradientId: `grad-${sourceIndex}-${targetIndex}`
                });
            }
        });
    });

    if (nodes.length === 0 || links.length === 0) return;
    const data = { nodes, links };

    // 5. RENDER D3
    const width = containerRef.current.clientWidth || 800;
    const height = 600; 
    
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current)
        .attr("width", width)
        .attr("height", height)
        .style("overflow", "visible")
        .on("click", () => setSelectedNodeIndex(null));

    const sankeyGenerator = d3Sankey()
        .nodeWidth(20)
        .nodePadding(12)
        .extent([[1, 1], [width - 1, height - 6]]);

    try {
        const { nodes: graphNodes, links: graphLinks } = sankeyGenerator(data);

        const defs = svg.append("defs");
        graphLinks.forEach((link, i) => {
            const gradient = defs.append("linearGradient")
                .attr("id", link.gradientId || `grad-${i}`)
                .attr("gradientUnits", "userSpaceOnUse")
                .attr("x1", link.source.x1)
                .attr("x2", link.target.x0);
            
            gradient.append("stop").attr("offset", "0%").attr("stop-color", "#94a3b8");
            gradient.append("stop").attr("offset", "100%").attr("stop-color", link.target.color);
        });

        // --- LINKS ---
        svg.append("g")
            .selectAll("path")
            .data(graphLinks)
            .join("path")
            .attr("d", sankeyLinkHorizontal())
            .attr("stroke", d => `url(#${d.gradientId})`)
            .attr("stroke-width", d => Math.max(1, d.width))
            .attr("fill", "none")
            .attr("class", "cursor-pointer")
            .attr("opacity", d => {
                if (selectedNodeIndex === null) return 0.3;
                const isConnected = d.source.index === selectedNodeIndex || d.target.index === selectedNodeIndex;
                return isConnected ? 0.8 : 0.05;
            })
            .style("transition", "opacity 0.3s ease")
            .on("mouseenter", function(event, d) {
                const sourceTotal = d.source.value;
                const percentageOfArchetype = ((d.value / sourceTotal) * 100).toFixed(1);
                const { x, y } = getRelativePosition(event);

                d3.select(this).attr("opacity", 0.9).attr("stroke-width", d.width + 2);
                
                setTooltip({
                    x, y,
                    content: (
                        <div className="space-y-1">
                            <div className="font-bold text-slate-100 border-b border-slate-600 pb-1 mb-1">
                                {d.source.name}
                            </div>
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                                <span className="text-slate-400">Leaning:</span>
                                <span className="font-semibold" style={{ color: d.target.color }}>{d.target.name}</span>
                                
                                <span className="text-slate-400">Contribution:</span>
                                <span className="text-white font-mono">{percentageOfArchetype}% of Archetype</span>
                                
                                <span className="text-slate-400">Voters:</span>
                                <span className="text-slate-300">{d.value.toLocaleString()}</span>
                            </div>
                        </div>
                    )
                });
            })
            .on("mousemove", (event) => {
                const { x, y } = getRelativePosition(event);
                setTooltip(prev => prev ? ({ ...prev, x, y }) : null);
            })
            .on("mouseleave", function(event, d) {
                const isConnected = selectedNodeIndex !== null && (d.source.index === selectedNodeIndex || d.target.index === selectedNodeIndex);
                const baseOpacity = selectedNodeIndex === null ? 0.3 : (isConnected ? 0.8 : 0.05);
                
                d3.select(this).attr("opacity", baseOpacity).attr("stroke-width", d.width);
                setTooltip(null);
            });

        // --- NODES ---
        const nodeGroup = svg.append("g")
            .selectAll("rect")
            .data(graphNodes)
            .join("g");

        nodeGroup.append("rect")
            .attr("x", d => d.x0)
            .attr("y", d => d.y0)
            .attr("height", d => Math.max(0, d.y1 - d.y0))
            .attr("width", d => d.x1 - d.x0)
            .attr("fill", d => d.color)
            .attr("rx", 2)
            .attr("class", "sankey-node cursor-pointer")
            .attr("stroke", d => selectedNodeIndex === d.index ? "white" : "none")
            .attr("stroke-width", 2)
            .attr("opacity", d => {
                if (selectedNodeIndex === null) return 1;
                const isSelected = d.index === selectedNodeIndex;
                const isNeighbor = d.sourceLinks?.some(l => l.target.index === selectedNodeIndex) || 
                                   d.targetLinks?.some(l => l.source.index === selectedNodeIndex);
                return (isSelected || isNeighbor) ? 1 : 0.3;
            })
            .on("click", (event, d) => {
                event.stopPropagation();
                setSelectedNodeIndex(current => current === d.index ? null : d.index);
            })
            .on("mouseenter", function(event, d) {
                const { x, y } = getRelativePosition(event);
                setTooltip({
                    x, y,
                    content: (
                        <div>
                            <div className="font-bold text-white mb-1">{d.name}</div>
                            <div className="text-xs text-slate-300">Total: {Math.round(d.value).toLocaleString()} voters</div>
                        </div>
                    )
                });
            })
            .on("mousemove", (event) => {
                const { x, y } = getRelativePosition(event);
                setTooltip(prev => prev ? ({ ...prev, x, y }) : null);
            })
            .on("mouseleave", () => setTooltip(null));

        nodeGroup.append("text")
            .attr("x", d => d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6)
            .attr("y", d => (d.y1 + d.y0) / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", d => d.x0 < width / 2 ? "start" : "end")
            .text(d => (d.value > (totalPopulation * 0.01)) ? d.name : "")
            .style("fill", "#e2e8f0")
            .style("font-size", "11px")
            .style("font-weight", "500")
            .style("pointer-events", "none")
            .style("opacity", d => {
                if (selectedNodeIndex === null) return 1;
                const isSelected = d.index === selectedNodeIndex;
                const isNeighbor = d.sourceLinks?.some(l => l.target.index === selectedNodeIndex) || 
                                   d.targetLinks?.some(l => l.source.index === selectedNodeIndex);
                return (isSelected || isNeighbor) ? 1 : 0.1;
            });

    } catch (e) {
        console.error("Sankey Layout Error:", e);
    }

  }, [archetypes, totalPopulation, predictedVoteShares, selectedNodeIndex]);

  // CALCULATION:
  // 1. Horizontal Flip: If x > width - 300px
  // 2. Vertical Flip: If y > height - 200px
  const isFlippedX = tooltip && (tooltip.x > (containerDim.width - 300));
  const isFlippedY = tooltip && (tooltip.y > (containerDim.height - 200));

  return (
    <div className="relative w-full" ref={containerRef}>
        <svg ref={svgRef} className="w-full h-[600px]"></svg>
        <AnimatePresence>
            {tooltip && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.1 }}
                    className="absolute z-50 pointer-events-none px-4 py-3 bg-slate-900/95 border border-slate-600 rounded-lg shadow-2xl backdrop-blur-sm text-sm text-white min-w-[200px]"
                    style={{ 
                        // HORIZONTAL LOGIC
                        left: isFlippedX ? tooltip.x - 15 : tooltip.x + 15,
                        x: isFlippedX ? "-100%" : "0%",

                        // VERTICAL LOGIC
                        // If near bottom: Anchor top to cursor-15, and use Motion 'y' to shift -100% (up)
                        // If normal: Anchor top to cursor+15, and Motion 'y' is 0% (down)
                        top: isFlippedY ? tooltip.y - 15 : tooltip.y + 15,
                        y: isFlippedY ? "-100%" : "0%"
                    }}
                >
                    {tooltip.content}
                </motion.div>
            )}
        </AnimatePresence>
    </div>
  );
};

// --- NEW COMPONENT: PSYCHOMETRIC SYNTHESIS DASHBOARD (ROW 2) ---
// This synthesizes the "Soft Psychology" of the electorate
const PsychometricSynthesis = ({ archetypes, totalPopulation }) => {
    const stats = useMemo(() => {
        let weightedRWA = 0; // Authority/Tradition
        let weightedCare = 0; // Empathy/Care
        let weightedOpenness = 0; 
        let weightedUncertainty = 0; // Need for Structure
        let totalPop = 0;

        archetypes.forEach(arch => {
            const pop = arch.estimated_population || 0;
            const psych = arch.psychographics || {};
            const mft = psych.mft_profile || {};
            const ocean = psych.ocean_profile || {};
            const drivers = psych.psych_drivers || {};

            totalPop += pop;
            
            // Synthesize Moral Foundations (Care vs Authority)
            weightedCare += ((mft.care_harm || 0.5) + (mft.fairness_cheating || 0.5)) / 2 * pop;
            weightedRWA += ((mft.authority_subversion || 0.5) + (mft.loyalty_betrayal || 0.5) + (mft.purity_sanctity || 0.5)) / 3 * pop;
            
            // Synthesize Cognitive Style
            weightedOpenness += (ocean.openness || 0.5) * pop;
            weightedUncertainty += (drivers.uncertainty_avoidance || 0.5) * pop;
        });

        const normalize = (val) => totalPop > 0 ? val / totalPop : 0.5;

        return {
            care: normalize(weightedCare),
            authority: normalize(weightedRWA),
            openness: normalize(weightedOpenness),
            uncertainty: normalize(weightedUncertainty)
        };
    }, [archetypes]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-full">
            {/* 1. MORAL COMPASS */}
            <div className="bg-slate-800/30 rounded p-4 border border-slate-700/30 flex flex-col justify-center">
                <div className="flex items-center mb-3">
                    <Scale size={16} className="text-teal-400 mr-2" />
                    <span className="text-xs font-bold text-slate-300 uppercase">Moral Compass</span>
                </div>
                <div className="relative pt-4 pb-2">
                    <div className="flex justify-between text-[10px] text-slate-400 font-bold uppercase mb-1">
                        <span>Progressive (Care)</span>
                        <span>Traditional (Order)</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full w-full relative overflow-hidden">
                        <div className="absolute top-0 bottom-0 bg-gradient-to-r from-teal-500 via-slate-500 to-blue-600 opacity-80 w-full"></div>
                        {/* Needle */}
                        <motion.div 
                            initial={{ left: "50%" }}
                            animate={{ left: `${(stats.authority / (stats.care + stats.authority)) * 100}%` }}
                            className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_10px_rgba(255,255,255,0.8)] z-10"
                        />
                    </div>
                    <div className="mt-2 text-center text-xs text-white">
                        {stats.care > stats.authority ? "Driven by Fairness & Empathy" : "Driven by Tradition & Stability"}
                    </div>
                </div>
            </div>

            {/* 2. COGNITIVE MODE */}
            <div className="bg-slate-800/30 rounded p-4 border border-slate-700/30 flex flex-col justify-center">
                <div className="flex items-center mb-3">
                    <Anchor size={16} className="text-indigo-400 mr-2" />
                    <span className="text-xs font-bold text-slate-300 uppercase">Cognitive Need</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                    <div className="text-center w-1/2">
                        <div className="text-xl font-bold text-white">{(stats.uncertainty * 100).toFixed(0)}</div>
                        <div className="text-[10px] text-slate-400">Structure Seeking</div>
                    </div>
                    <div className="h-8 w-px bg-slate-700"></div>
                    <div className="text-center w-1/2">
                        <div className="text-xl font-bold text-white">{(stats.openness * 100).toFixed(0)}</div>
                        <div className="text-[10px] text-slate-400">Novelty Seeking</div>
                    </div>
                </div>
                <div className="mt-2 text-center text-xs text-indigo-300">
                    {stats.uncertainty > 0.6 ? "Electorate demands concrete plans." : "Electorate open to big ideas."}
                </div>
            </div>

            {/* 3. CULTURAL VIBE */}
            <div className="bg-slate-800/30 rounded p-4 border border-slate-700/30 flex flex-col justify-center">
                <div className="flex items-center mb-3">
                    <Lightbulb size={16} className="text-amber-400 mr-2" />
                    <span className="text-xs font-bold text-slate-300 uppercase">Cultural Vibe</span>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-400">Status Anxiety</span>
                        <div className="w-24 bg-slate-700 h-1.5 rounded-full">
                            <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: `${stats.uncertainty * 90}%` }}></div>
                        </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-400">System Trust</span>
                        <div className="w-24 bg-slate-700 h-1.5 rounded-full">
                             {/* Heuristic: Higher RWA usually correlates with system trust in this model unless populist */}
                            <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${stats.authority * 85}%` }}></div>
                        </div>
                    </div>
                     <div className="text-center text-[10px] text-slate-500 italic mt-1">
                        Synthesized from VALS & RWA profiles
                    </div>
                </div>
            </div>
        </div>
    );
};
// --- COMPONENT: STRATEGIC TARGETING MATRIX (Quantitatively Calibrated) ---
const StrategicTargetingMatrix = ({ archetypes, totalPopulation }) => {
    const [selectedNode, setSelectedNode] = useState(null);
    const [hoveredNode, setHoveredNode] = useState(null);
    const [focusedParty, setFocusedParty] = useState("Liberal Democrats"); 
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const containerRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (!archetypes || archetypes.length === 0) return null;

    const sortedArchetypes = [...archetypes].sort((a, b) => 
        (b.estimated_population || 0) - (a.estimated_population || 0)
    );

    // 1. POSITIONING LOGIC
    // Stable hash to prevent "jitter" on re-renders, only moving when data/party changes.
    const getStableOffset = (seedStr) => {
        let hash = 0;
        for (let i = 0; i < seedStr.length; i++) {
            hash = seedStr.charCodeAt(i) + ((hash << 5) - hash);
        }
        // Reduced Variance: +/- 6% to ensure tight clustering without overlap
        const x = (hash % 1200) / 100 - 6; 
        const y = ((hash * 31) % 1200) / 100 - 6;
        return { x, y };
    };

    // Clamp values to strictly 12% - 88% to prevent clipping with axes
    const clamp = (val) => Math.min(Math.max(val, 12), 88);

    // 2. STRATEGIC CLASSIFICATION ENGINE (The "Quant" Model)
    const getStrategicData = (arch) => {
        const archKey = arch.category || "UNKNOWN";
        const propensities = ARCHETYPE_PROPENSITIES[archKey] || {};
        
        // INPUTS
        const affinity = propensities[focusedParty] || 0; // The Sankey Correlation
        const turnout = arch.avg_turnout_probability || 0;
        const persuadability = arch.avg_persuadability_score || 0;
        
        const offset = getStableOffset(arch.name);

        // THRESHOLDS (Calibrated for UK Multi-Party System)
        const THRESHOLD_ALIGNMENT = 0.30; // 30% Affinity = Base in a 5-party system
        const THRESHOLD_TURNOUT = 0.60;   // 60% = Reliable Voter
        const THRESHOLD_SWING = 0.45;     // 45% = Open to persuasion

        let xBase, yBase, strategy;

        // --- QUADRANT LOGIC ---

        // 1. BASE / GOTV (High Alignment)
        if (affinity >= THRESHOLD_ALIGNMENT) {
            if (turnout >= THRESHOLD_TURNOUT) {
                // QUADRANT: TOP LEFT (Bank)
                xBase = 25; 
                yBase = 75;
                strategy = { 
                    title: "Core Base (Bank)", 
                    color: "text-emerald-400", 
                    bg: "bg-emerald-500/10", 
                    border: "border-emerald-500/30",
                    dot: "bg-emerald-500",
                    desc: "Aligned & Reliable. Protect." 
                };
            } else {
                // QUADRANT: BOTTOM RIGHT (Mobilise)
                // Note: Strategically, low turnout base is plotted Bottom-Right 
                // to differentiate from Opposition (Bottom-Left)
                xBase = 75; 
                yBase = 25;
                strategy = { 
                    title: "Base GOTV (Mobilise)", 
                    color: "text-blue-400", 
                    bg: "bg-blue-500/10", 
                    border: "border-blue-500/30",
                    dot: "bg-blue-500",
                    desc: "Aligned but needs turnout push." 
                };
            }
        }
        // 2. OPPOSITION (Low Alignment, Low Swing)
        else if (affinity < 0.15 && persuadability < THRESHOLD_SWING) {
            // QUADRANT: BOTTOM LEFT (Ignore)
            xBase = 25; 
            yBase = 25;
            strategy = { 
                title: "Hard Opposition", 
                color: "text-red-400", 
                bg: "bg-red-500/10", 
                border: "border-red-500/30",
                dot: "bg-red-500",
                desc: "Entrenched. Do not resource." 
            };
        }
        // 3. BATTLEGROUND (The Rest)
        else {
             // QUADRANT: TOP RIGHT (Persuade)
             // High Turnout OR High Swing
             if (turnout >= 0.5) {
                xBase = 75; 
                yBase = 75;
                strategy = { 
                    title: "Prime Battleground", 
                    color: "text-purple-400", 
                    bg: "bg-purple-500/10", 
                    border: "border-purple-500/30",
                    dot: "bg-purple-500",
                    desc: "High yield targets." 
                };
             } else {
                 // MIDDLE (Expansion)
                 xBase = 50; 
                 yBase = 50;
                 strategy = { 
                    title: "Expansion Target", 
                    color: "text-pink-400", 
                    bg: "bg-pink-500/10", 
                    border: "border-pink-500/30",
                    dot: "bg-pink-500",
                    desc: "Persuadable but low turnout." 
                };
             }
        }

        return { 
            x: clamp(xBase + offset.x), 
            y: clamp(yBase + offset.y), 
            strategy,
            affinity,
            turnout
        };
    };

    return (
        <div className="flex flex-col h-[650px] w-full bg-[#0f172a] rounded-xl border border-slate-800 overflow-hidden shadow-sm relative" ref={containerRef}>
            
            {/* 0. CONTROLS HEADER */}
            <div className="flex-none flex items-center justify-between px-6 py-3 border-b border-slate-800 bg-slate-900/90 backdrop-blur z-30">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                    <Crosshair size={14} />
                    Strategic Perspective:
                </div>
                
                <div className="relative">
                    <button 
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="flex items-center space-x-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white text-xs font-medium py-1.5 px-3 rounded transition-all min-w-[160px] justify-between"
                    >
                        <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: PARTY_COLORS[focusedParty] || '#cbd5e1' }} />
                            {focusedParty}
                        </div>
                        <ChevronDown size={14} className={`transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                        {isDropdownOpen && (
                            <motion.div 
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 5 }}
                                className="absolute right-0 mt-1 w-48 bg-slate-800 border border-slate-700 rounded shadow-xl z-50 overflow-hidden"
                            >
                                {Object.keys(PARTY_COLORS).filter(p => p !== 'Other/Abstain').map((party) => (
                                    <button
                                        key={party}
                                        onClick={() => {
                                            setFocusedParty(party);
                                            setIsDropdownOpen(false);
                                            setSelectedNode(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-xs text-slate-300 hover:bg-slate-700 hover:text-white flex items-center justify-between group"
                                    >
                                        <div className="flex items-center">
                                            <div className="w-2 h-2 rounded-full mr-2 opacity-50 group-hover:opacity-100" style={{ backgroundColor: PARTY_COLORS[party] }} />
                                            {party}
                                        </div>
                                        {focusedParty === party && <Check size={12} className="text-emerald-400" />}
                                    </button>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* 1. CHART AREA */}
            <div className="flex-1 relative w-full min-h-0 overflow-hidden bg-[#0f172a]" onClick={() => setSelectedNode(null)}>
                
                {/* Background Grid & Labels */}
                <div className="absolute inset-4 pointer-events-none z-0">
                    <div className="w-full h-full border-l border-b border-slate-700/50 relative grid grid-cols-2 grid-rows-2">
                        {/* Quadrant Labels */}
                        <div className="p-2 border-r border-b border-dashed border-slate-700/30 bg-emerald-500/[0.02] flex items-start justify-start">
                            <span className="text-[10px] font-bold text-emerald-500/40 uppercase tracking-widest">Base (Reassure)</span>
                        </div>
                        <div className="p-2 border-b border-dashed border-slate-700/30 bg-purple-500/[0.02] flex items-start justify-end">
                            <span className="text-[10px] font-bold text-purple-500/40 uppercase tracking-widest">Battleground (Persuade)</span>
                        </div>
                        <div className="p-2 border-r border-dashed border-slate-700/30 bg-slate-500/[0.02] flex items-end justify-start">
                            <span className="text-[10px] font-bold text-slate-500/40 uppercase tracking-widest">Opposition (Ignore)</span>
                        </div>
                        <div className="p-2 bg-blue-500/[0.02] flex items-end justify-end">
                            <span className="text-[10px] font-bold text-blue-500/40 uppercase tracking-widest">GOTV (Mobilise)</span>
                        </div>

                        {/* Axis Labels */}
                        <div className="absolute -left-5 top-1/2 -translate-y-1/2 -rotate-90 text-[9px] font-bold text-slate-600 uppercase tracking-widest whitespace-nowrap">
                            Turnout Probability →
                        </div>
                        <div className="absolute bottom-[-18px] left-1/2 -translate-x-1/2 text-[9px] font-bold text-slate-600 uppercase tracking-widest whitespace-nowrap">
                            Strategic Opportunity →
                        </div>
                    </div>
                </div>

                {/* The Graph SVG */}
                <svg className="w-full h-full overflow-visible relative z-10 px-4 py-4">
                    <AnimatePresence mode="popLayout">
                    {sortedArchetypes.map((arch) => {
                        const r = Math.max(10, Math.sqrt((arch.estimated_population || 0) / totalPopulation) * 60); 
                        const data = getStrategicData(arch);
                        
                        // Invert Y for SVG (0 is top)
                        const svgY = 100 - data.y;
                        const svgX = data.x;

                        const isSelected = selectedNode?.name === arch.name;
                        const isHovered = hoveredNode === arch.name;
                        const isDimmed = (selectedNode && !isSelected);

                        // Colors
                        let fill = "#64748b";
                        if (data.strategy.color.includes('emerald')) fill = "#10b981";
                        if (data.strategy.color.includes('purple')) fill = "#a855f7";
                        if (data.strategy.color.includes('blue')) fill = "#3b82f6";
                        if (data.strategy.color.includes('pink')) fill = "#ec4899";
                        if (data.strategy.color.includes('red')) fill = "#ef4444";

                        return (
                            <g key={arch.name} onClick={(e) => { e.stopPropagation(); setSelectedNode(arch); }}>
                                {/* Halo */}
                                {isSelected && (
                                    <circle cx={`${svgX}%`} cy={`${svgY}%`} r={r + 5} fill="none" stroke="white" strokeWidth="2" opacity="0.5" />
                                )}
                                
                                <motion.circle
                                    layoutId={`bubble-${arch.name}`} 
                                    cx={`${svgX}%`}
                                    cy={`${svgY}%`}
                                    r={r}
                                    fill={fill}
                                    fillOpacity={isSelected || isHovered ? 0.95 : 0.75}
                                    stroke="white"
                                    strokeWidth={isSelected ? 3 : 1}
                                    initial={false}
                                    animate={{ 
                                        cx: `${svgX}%`,
                                        cy: `${svgY}%`,
                                        fill: fill,
                                        opacity: isDimmed ? 0.2 : 1
                                    }}
                                    // Use 'tween' with easeInOut for smoother, more "fluid" movement than spring
                                    transition={{ 
                                        type: "tween", 
                                        ease: "easeInOut",
                                        duration: 0.6
                                    }}
                                    className="cursor-pointer drop-shadow-lg"
                                    onMouseEnter={() => setHoveredNode(arch.name)}
                                    onMouseLeave={() => setHoveredNode(null)}
                                />
                            </g>
                        )
                    })}
                    </AnimatePresence>
                </svg>
            </div>

            {/* 2. FOOTER */}
            <div className="flex-none h-[190px] bg-[#111827] border-t border-slate-800 p-5 relative z-30 shadow-[0_-5px_15px_rgba(0,0,0,0.3)]">
                
                <AnimatePresence mode="wait">
                    {selectedNode ? (
                        // --- SELECTED STATE ---
                        <motion.div 
                            key="detail"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="h-full flex flex-col justify-center"
                        >
                             {(() => {
                                const { strategy, affinity } = getStrategicData(selectedNode);
                                return (
                                    <div className="w-full">
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                                                    Selected Segment
                                                </div>
                                                <h3 className="text-lg font-bold text-white flex items-center gap-3">
                                                    {selectedNode.name}
                                                    <span className="text-xs font-normal text-slate-400 bg-slate-800 px-2 py-0.5 rounded-full border border-slate-700">
                                                        {(selectedNode.estimated_population / 1000).toFixed(1)}k Voters
                                                    </span>
                                                </h3>
                                            </div>
                                            <button onClick={() => setSelectedNode(null)} className="p-1.5 hover:bg-slate-800 rounded-full transition-colors text-slate-400">
                                                <X size={20} />
                                            </button>
                                        </div>

                                        {/* Strategy Box */}
                                        <div className={`flex items-center p-3 rounded-lg border mb-4 ${strategy.bg} ${strategy.border}`}>
                                            <div className={`w-3 h-3 rounded-full mr-3 ${strategy.dot} shadow-[0_0_8px_rgba(255,255,255,0.4)]`}></div>
                                            <div>
                                                <div className={`text-sm font-bold ${strategy.color}`}>
                                                    {strategy.title}
                                                </div>
                                                <div className="text-xs text-slate-300">
                                                    {strategy.desc}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Metrics Row */}
                                        <div className="flex items-center gap-6 text-xs px-1">
                                            <div className="flex flex-col">
                                                <span className="text-slate-500 uppercase text-[9px] mb-0.5">Persuadability</span>
                                                <span className="text-white font-mono text-sm font-bold">{(selectedNode.avg_persuadability_score * 100).toFixed(0)}%</span>
                                            </div>
                                            <div className="h-6 w-px bg-slate-700"></div>
                                            <div className="flex flex-col">
                                                <span className="text-slate-500 uppercase text-[9px] mb-0.5">Turnout Prob.</span>
                                                <span className="text-white font-mono text-sm font-bold">{(selectedNode.avg_turnout_probability * 100).toFixed(0)}%</span>
                                            </div>
                                            <div className="h-6 w-px bg-slate-700"></div>
                                            <div className="flex flex-col">
                                                 <span className="text-slate-500 uppercase text-[9px] mb-0.5">{focusedParty} Affinity</span>
                                                 <span className={`font-mono text-sm font-bold ${affinity > 0.4 ? 'text-green-400' : 'text-slate-200'}`}>
                                                    {(affinity * 100).toFixed(0)}%
                                                 </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                             })()}
                        </motion.div>
                    ) : (
                        // --- DEFAULT LEGEND STATE ---
                        <motion.div 
                            key="legend"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="h-full flex flex-col"
                        >
                             <div className="text-center mb-4 border-b border-slate-800/50 pb-2">
                                <span className="text-xs text-slate-400 font-light flex items-center justify-center gap-2">
                                    <Compass size={12} />
                                    Viewing battlefield from <span style={{color: PARTY_COLORS[focusedParty]}} className="font-bold">{focusedParty}</span> perspective
                                </span>
                             </div>
                             
                             <div className="grid grid-cols-2 gap-3 flex-grow">
                                <div className="flex items-center px-3 py-1.5 rounded bg-[#3b0764]/20 border border-purple-500/20">
                                    <span className="w-2 h-2 rounded-full bg-purple-500 mr-2 shadow-[0_0_8px_rgba(168,85,247,0.5)]"></span>
                                    <div>
                                        <div className="text-xs font-bold text-slate-200">Battleground</div>
                                        <div className="text-[9px] text-slate-500 leading-none">High Swing / High Turnout</div>
                                    </div>
                                </div>
                                <div className="flex items-center px-3 py-1.5 rounded bg-[#172554]/20 border border-blue-500/20">
                                    <span className="w-2 h-2 rounded-full bg-blue-500 mr-2 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></span>
                                    <div>
                                        <div className="text-xs font-bold text-slate-200">Base GOTV</div>
                                        <div className="text-[9px] text-slate-500 leading-none">Aligned / Needs Mobilisation</div>
                                    </div>
                                </div>
                                <div className="flex items-center px-3 py-1.5 rounded bg-[#064e3b]/20 border border-emerald-500/20">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
                                    <div>
                                        <div className="text-xs font-bold text-slate-200">Core Base</div>
                                        <div className="text-[9px] text-slate-500 leading-none">Aligned / Reliable</div>
                                    </div>
                                </div>
                                <div className="flex items-center px-3 py-1.5 rounded bg-red-900/10 border border-red-500/20">
                                    <span className="w-2 h-2 rounded-full bg-red-500 mr-2"></span>
                                    <div>
                                        <div className="text-xs font-bold text-slate-200">Opposition</div>
                                        <div className="text-[9px] text-slate-500 leading-none">Low Affinity / Low Swing</div>
                                    </div>
                                </div>
                             </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};
// --- COMPONENT: AGGREGATED CAMPAIGN PRIORITIES ---
const CampaignPriorities = ({ archetypes }) => {
    const { topIssues, topChannels } = useMemo(() => {
        const issueMap = {};
        const channelMap = {};
        let totalPop = 0;

        archetypes.forEach(arch => {
            const pop = arch.estimated_population || 0;
            totalPop += pop;

            // Weight issues
            const issues = arch.issue_priorities || {};
            Object.entries(issues).forEach(([issue, score]) => {
                issueMap[issue] = (issueMap[issue] || 0) + (score * pop);
            });

            // Weight channels
            const channels = arch.channel_receptivity || {};
            Object.entries(channels).forEach(([channel, score]) => {
                channelMap[channel] = (channelMap[channel] || 0) + (score * pop);
            });
        });

        // Normalize
        const normalizedIssues = Object.entries(issueMap)
            .map(([k, v]) => ({ key: k, score: totalPop > 0 ? v / totalPop : 0 }))
            .sort((a, b) => b.score - a.score)
            .slice(0, 5);

        const normalizedChannels = Object.entries(channelMap)
            .map(([k, v]) => ({ key: k, score: totalPop > 0 ? v / totalPop : 0 }))
            .sort((a, b) => b.score - a.score)
            .slice(0, 4);

        return { topIssues: normalizedIssues, topChannels: normalizedChannels };
    }, [archetypes]);

    const formatName = (name) => name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
            <div className="bg-slate-800/30 rounded p-4 border border-slate-700/30 flex flex-col">
                <div className="text-xs font-bold text-slate-400 uppercase mb-3 flex items-center">
                    <Megaphone size={14} className="mr-2 text-orange-400" />
                    Coalition Unifiers (Top Issues)
                </div>
                <div className="space-y-2 flex-grow">
                    {topIssues.map((item, i) => (
                        <div key={item.key} className="flex items-center justify-between text-xs">
                            <span className="text-white">{formatName(item.key)}</span>
                            <div className="flex items-center w-1/2">
                                <div className="w-full bg-slate-700 rounded-full h-1.5 mr-2">
                                    <div className="bg-orange-500 h-1.5 rounded-full" style={{ width: `${item.score * 100}%` }}></div>
                                </div>
                                <span className="text-slate-400 w-8 text-right">{(item.score * 100).toFixed(0)}%</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-slate-800/30 rounded p-4 border border-slate-700/30 flex flex-col">
                <div className="text-xs font-bold text-slate-400 uppercase mb-3 flex items-center">
                    <Layers size={14} className="mr-2 text-cyan-400" />
                    Efficient Channels
                </div>
                <div className="space-y-2 flex-grow">
                    {topChannels.map((item, i) => (
                        <div key={item.key} className="flex items-center justify-between text-xs">
                            <span className="text-white">{formatName(item.key)}</span>
                            <div className="flex items-center w-1/2">
                                <div className="w-full bg-slate-700 rounded-full h-1.5 mr-2">
                                    <div className="bg-cyan-500 h-1.5 rounded-full" style={{ width: `${item.score * 100}%` }}></div>
                                </div>
                                <span className="text-slate-400 w-8 text-right">{(item.score * 100).toFixed(0)}%</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- COMPONENT: BATTLEGROUND TACTICS (Summary Data) ---
const BattlegroundTactics = ({ targets = [], defenses = [] }) => {
    // UPDATE: Render if EITHER targets OR defenses exist.
    if (targets.length === 0 && defenses.length === 0) return (
        <div className="flex items-center justify-center h-full text-xs text-slate-500 italic p-4">
            No battleground data available.
        </div>
    );

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
            {/* OFFENSE LIST */}
            <div className="bg-emerald-900/20 rounded-lg p-4 border border-emerald-500/30">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider flex items-center">
                        <Crosshair size={14} className="mr-2" />
                        Top Offensive Targets
                    </h4>
                    <span className="text-[10px] text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded">GAIN PRIORITY</span>
                </div>
                <div className="space-y-2">
                    {targets.length > 0 ? targets.slice(0, 5).map((ward, i) => (
                        <div key={i} className="flex justify-between items-center text-xs border-b border-emerald-500/10 pb-1 last:border-0">
                            <span className="text-slate-200 font-medium">{ward.ward_name}</span>
                            <span className="text-emerald-400 font-bold">{ward.score ? ward.score.toFixed(1) : '-'} <span className="text-[9px] text-slate-500 font-normal">SCR</span></span>
                        </div>
                    )) : (
                        <div className="text-[10px] text-slate-500 italic">No offensive targets identified.</div>
                    )}
                </div>
            </div>

            {/* DEFENSE LIST */}
            <div className="bg-red-900/20 rounded-lg p-4 border border-red-500/30">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider flex items-center">
                        <Shield size={14} className="mr-2" />
                        Critical Defense
                    </h4>
                    <span className="text-[10px] text-red-500 bg-red-500/10 px-2 py-0.5 rounded">HOLD URGENT</span>
                </div>
                <div className="space-y-2">
                    {defenses.length > 0 ? defenses.slice(0, 5).map((ward, i) => (
                        <div key={i} className="flex justify-between items-center text-xs border-b border-red-500/10 pb-1 last:border-0">
                            <span className="text-slate-200 font-medium">{ward.ward_name}</span>
                            <span className="text-red-400 font-bold">{ward.score ? ward.score.toFixed(1) : '-'} <span className="text-[9px] text-slate-500 font-normal">SCR</span></span>
                        </div>
                    )) : (
                        <div className="text-[10px] text-slate-500 italic">No defensive targets identified.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- COMPONENT: PSYCHOGRAPHIC ROLLUP (Summary Data) ---
const PsychographicRollup = ({ frames, pathways }) => {
    if (!frames && !pathways) return null;

    const topFrames = Object.entries(frames || {})
        .sort(([,a], [,b]) => b - a)
        .slice(0, 1); 
    
    const topPathway = Object.entries(pathways || {})
        .sort(([,a], [,b]) => b - a)
        .slice(0, 1);

    return (
        <div className="flex flex-col h-full justify-center space-y-4">
            {topFrames.map(([name, count]) => (
                <div key="frame" className="bg-slate-800/50 p-3 rounded border-l-2 border-purple-500">
                    <div className="text-[10px] text-slate-400 uppercase mb-1">Dominant Moral Frame</div>
                    <div className="text-sm font-bold text-white">{name}</div>
                    <div className="text-[10px] text-slate-500 mt-1">Detected in {count} wards</div>
                </div>
            ))}
            {topPathway.map(([name, count]) => (
                <div key="path" className="bg-slate-800/50 p-3 rounded border-l-2 border-blue-500">
                    <div className="text-[10px] text-slate-400 uppercase mb-1">Primary Persuasion Style</div>
                    <div className="text-sm font-bold text-white">{name}</div>
                    <div className="text-[10px] text-slate-500 mt-1">Effective in {count} wards</div>
                </div>
            ))}
        </div>
    );
};

// --- UPDATED DASHBOARD COMPONENT ---
const CampaignDashboard = ({ campaignData }) => {
    // 1. ROBUST DATA ACCESS
    const summaryData = {...campaignData?.swingData?.summary, ...campaignData?.predictionData?.summary}  ?? {};
    console.hidden(summaryData);
    const archetypes = campaignData?.archetypeData;
    
    if (!archetypes || archetypes.length === 0) {
        return (
            <div className="p-12 text-center text-slate-400 border border-dashed border-slate-700 rounded-lg">
                <p>No campaign summary data available.</p>
                <p className="text-xs text-slate-500 mt-2">Ensure data is loaded from 'campaigns/&#123;id&#125;/archetypes/campaign_summary'</p>
            </div>
        );
    }

    // 2. ROBUST POPULATION CALCULATION
    const calculatedTotalPop = archetypes.reduce((sum, arch) => sum + (arch.estimated_population || 0), 0);
    const apiTotalPop = campaignData.total_campaign_population_16_plus || campaignData.total_campaign_population || 0;
    const totalPop = summaryData.campaign_total_voters || (apiTotalPop > 0 ? apiTotalPop : calculatedTotalPop);
    
    const totalWards = summaryData.total_wards_analyzed || 
                       campaignData.total_wards_in_summary || 
                       campaignData.wardsProcessed || 0;

    // 3. Synthesize Campaign-Wide Metrics
    const campaignStats = useMemo(() => {
        let weightedAge = 0;
        let weightedIncome = 0;
        let weightedHouse = 0;
        let weightedTurnout = 0;
        let weightedPersuadability = 0;
        let totalSegmentedPop = 0;

        archetypes.forEach(arch => {
            const pop = arch.estimated_population || 0;
            totalSegmentedPop += pop;
            weightedAge += ((arch.avg_age || 0) * pop);
            weightedIncome += ((arch.avg_income || 0) * pop);
            weightedHouse += ((arch.avg_house_value || 0) * pop);
            weightedTurnout += ((arch.avg_turnout_probability || 0) * pop);
            weightedPersuadability += ((arch.avg_persuadability_score || 0) * pop);
        });

        const safeDiv = (num) => totalSegmentedPop > 0 ? num / totalSegmentedPop : 0;

        return {
            avgAge: safeDiv(weightedAge),
            avgIncome: safeDiv(weightedIncome),
            avgHouse: safeDiv(weightedHouse),
            projectedTurnout: safeDiv(weightedTurnout),
            overallPersuadability: safeDiv(weightedPersuadability),
            coverage: totalPop > 0 ? totalSegmentedPop / totalPop : 0
        };
    }, [archetypes, totalPop]);

    // 4. Identify Strategic Segments
    const strategicSegments = useMemo(() => {
        const sorted = [...archetypes].sort((a,b) => (b.estimated_population || 0) - (a.estimated_population || 0));
        
        const battleground = archetypes
            .filter(a => (a.avg_persuadability_score > 0.55) && (a.estimated_population > (totalPop * 0.05)))
            .sort((a, b) => (b.avg_persuadability_score * b.estimated_population) - (a.avg_persuadability_score * a.estimated_population))
            .slice(0, 3);

        return { dominant: sorted[0], battleground };
    }, [archetypes, totalPop]);

    const dominantPct = strategicSegments.dominant && totalPop > 0 
        ? ((strategicSegments.dominant.estimated_population / totalPop) * 100).toFixed(1)
        : "0.0";

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            
            {/* ROW 1: HIGH-LEVEL STRATEGIC KPI GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Metric 1: Scale */}
                <Panel className="relative overflow-hidden group h-full">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Users size={64} className="text-blue-400" />
                    </div>
                    <div className="flex flex-col h-full justify-between relative z-10">
                        <div>
                            <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">Campaign Scale</span>
                            <div className="text-3xl font-bold text-white mt-1">{totalPop.toLocaleString()}</div>
                            <div className="text-xs text-slate-400 mt-1">Population across {totalWards} wards</div>
                        </div>
                        {/* V3.6 ADDITION: Winability Score from Summary */}
                        {summaryData.average_winability && (
                            <div className="mt-4 pt-3 border-t border-white/5">
                                <div className="flex justify-between items-center text-xs text-slate-400">
                                    <span>Avg Winability</span>
                                    <span className={`font-bold ${summaryData.average_winability > 0.5 ? 'text-green-400' : 'text-orange-400'}`}>
                                        {(summaryData.average_winability * 100).toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </Panel>

                {/* Metric 2: Swing Potential */}
                <Panel className="relative overflow-hidden group h-full">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <TrendingUp size={64} className="text-purple-400" />
                    </div>
                    <div className="flex flex-col h-full justify-between relative z-10">
                        <div>
                            <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Swing Pool</span>
                            <div className="text-3xl font-bold text-white mt-1">
                                {summaryData.campaign_total_swing_pool 
                                    ? summaryData.campaign_total_swing_pool.toLocaleString() 
                                    : (campaignStats.overallPersuadability * totalPop).toFixed(0)}
                            </div>
                            <div className="text-xs text-slate-400 mt-1">
                                {summaryData.campaign_total_swing_pool 
                                    ? "Identified Switch/Squeeze voters" 
                                    : "Estimated persuadable voters"}
                            </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-white/5">
                            <div className="flex items-center space-x-2 text-xs text-purple-300">
                                <AlertCircle size={12} />
                                <span>
                                    {summaryData.campaign_total_swing_pool 
                                        ? `${((summaryData.campaign_total_swing_pool / totalPop) * 100).toFixed(1)}% of electorate` 
                                        : "High volatility environment"}
                                </span>
                            </div>
                        </div>
                    </div>
                </Panel>

                {/* Metric 3: Turnout */}
                <Panel className="relative overflow-hidden group h-full">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Vote size={64} className="text-green-400" />
                    </div>
                    <div className="flex flex-col h-full justify-between relative z-10">
                        <div>
                            <span className="text-xs font-bold text-green-400 uppercase tracking-wider">Base & Turnout</span>
                            <div className="text-3xl font-bold text-white mt-1">
                                {summaryData.campaign_total_supporters 
                                    ? summaryData.campaign_total_supporters.toLocaleString()
                                    : `${(campaignStats.projectedTurnout * 100).toFixed(1)}%`}
                            </div>
                            <div className="text-xs text-slate-400 mt-1">
                                {summaryData.campaign_total_supporters ? "Identified Supporters" : "Weighted turnout prob."}
                            </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-white/5">
                            <ProgressBar 
                                value={campaignStats.projectedTurnout} 
                                colorClass="bg-green-500" 
                                label="Turnout Model" 
                                subLabel="High"
                            />
                        </div>
                    </div>
                </Panel>

                {/* Metric 4: Dominant Group */}
                <Panel className="relative overflow-hidden group h-full">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Target size={64} className="text-orange-400" />
                    </div>
                    <div className="flex flex-col h-full justify-between relative z-10">
                        <div>
                            <span className="text-xs font-bold text-orange-400 uppercase tracking-wider">Dominant Segment</span>
                            <div className="text-xl font-bold text-white mt-1 truncate pr-2">
                                {strategicSegments.dominant?.name || "N/A"}
                            </div>
                            <div className="text-xs text-slate-400 mt-1">
                                {strategicSegments.dominant 
                                    ? `${dominantPct}% of electorate`
                                    : "No data"}
                            </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-white/5">
                             <div className="flex flex-wrap gap-1">
                                {strategicSegments.dominant?.issue_priorities && Object.keys(strategicSegments.dominant.issue_priorities).slice(0,2).map(issue => (
                                    <span key={issue} className="text-[10px] px-1.5 py-0.5 bg-orange-500/10 text-orange-300 rounded border border-orange-500/20 uppercase">
                                        {issue.replace('_', ' ')}
                                    </span>
                                ))}
                             </div>
                        </div>
                    </div>
                </Panel>
            </div>

            {/* ROW 2: THE ELECTORATE'S MINDSET (NEW PSYCHOMETRIC SYNTHESIS) */}
            <Panel 
                title="The Electorate's Mindset" 
                icon={<Brain size={20} className="text-indigo-400" />} 
                className="w-full"
            >
                <PsychometricSynthesis archetypes={archetypes} totalPopulation={totalPop} />
            </Panel>

            {/* ROW 3: STRATEGIC COMMAND CENTER */}
            {/* UPDATE: Conditional check now allows rendering if EITHER target or defense wards exist */}
            {((summaryData.key_target_wards && summaryData.key_target_wards.length > 0) || 
              (summaryData.key_defend_wards && summaryData.key_defend_wards.length > 0)) && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Panel 
                        title="Battleground Command" 
                        icon={<MapPin size={20} className="text-red-400" />} 
                        className="lg:col-span-2"
                    >
                        <BattlegroundTactics 
                            targets={summaryData.key_target_wards} 
                            defenses={summaryData.key_defend_wards} 
                        />
                    </Panel>
                    
                    <Panel 
                        title="Psychographic Dominance" 
                        icon={<Brain size={20} className="text-purple-400" />} 
                    >
                        <PsychographicRollup 
                            frames={summaryData.dominant_messaging_frames} 
                            pathways={summaryData.dominant_persuasion_pathways} 
                        />
                    </Panel>
                </div>
            )}

            {/* ROW 4: VISUALIZATION & DEEP DIVES */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                
                {/* COL 1: HYBRID SANKEY DIAGRAM (Spans 8 cols) */}
                <Panel 
                    title="Hybridized Voter Flow (Model Adjusted)" 
                    icon={<Compass size={20} className="text-teal-400" />} 
                    className="lg:col-span-8 min-h-[500px] flex flex-col"
                >
                    <div className="mb-4 flex items-center justify-between">
                         <p className="text-sm text-slate-400">
                            Visualizing the flow from <strong>Archetype Segments</strong> to <strong>Predicted Vote</strong>. 
                            <span className="block text-xs text-slate-500 mt-1">Flows are calibrated using the Bayesian prediction model output to reflect specific campaign dynamics.</span>
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-slate-500">
                            <span className="flex items-center"><div className="w-2 h-2 rounded-full bg-slate-500 mr-1"/> Archetype</span>
                            <span className="flex items-center"><div className="w-2 h-2 rounded-full bg-blue-500 mr-1"/> Predicted Party Vote</span>
                        </div>
                    </div>
                   
                    <div className="flex-grow flex items-center justify-center bg-slate-900/20 rounded-lg border border-slate-700/30 p-4">
                         {/* Pass vote_shares to calibrate the flows */}
                         <SankeyDiagram 
                             archetypes={archetypes} 
                             totalPopulation={totalPop} 
                             predictedVoteShares={summaryData.vote_shares} 
                         />
                    </div>
                </Panel>

                {/* COL 2: TARGETING MATRIX (Spans 4 cols) */}
                <Panel 
                    title="Strategic Targeting Matrix" 
                    icon={<BarChart2 size={20} className="text-pink-400" />} 
                    className="lg:col-span-4 min-h-[500px]"
                >
                    <p className="text-xs text-slate-400 mb-4">
                        Mapping segments by <strong>Turnout</strong> (Y) vs <strong>Persuadability</strong> (X).
                    </p>
                    <StrategicTargetingMatrix archetypes={archetypes} totalPopulation={totalPop} />
                </Panel>
            </div>

            {/* ROW 5: OPTIMIZATION & DEMOGRAPHICS */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* COL 1: CAMPAIGN PRIORITIES (Spans 6 cols) */}
                <Panel 
                    title="Campaign-Wide Optimizers" 
                    icon={<Zap size={20} className="text-yellow-400" />} 
                    className="lg:col-span-6"
                >
                    <p className="text-xs text-slate-400 mb-4">
                        Weighted aggregation of all archetype data to identify the <strong>strongest unifying messages</strong> and <strong>most efficient channels</strong>.
                    </p>
                    <CampaignPriorities archetypes={archetypes} />
                </Panel>

                {/* COL 2: BATTLEGROUND SEGMENTS (Spans 6 cols) */}
                <Panel 
                    title="Battleground Targets" 
                    icon={<Crosshair size={20} className="text-red-400" />} 
                    className="lg:col-span-6"
                >
                    <p className="text-xs text-slate-400 mb-3">High population segments with high persuadability (&gt;55%).</p>
                    <div className="space-y-3">
                        {strategicSegments.battleground.map(arch => (
                            <div key={arch.category} className="group cursor-pointer hover:bg-slate-700/30 transition-colors p-3 rounded-lg border border-slate-700/50">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <div className="text-sm font-semibold text-white group-hover:text-sky-400 transition-colors">{arch.name}</div>
                                        <div className="text-[10px] text-slate-400">{arch.main_segment}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs font-bold text-white">{(arch.estimated_population || 0).toLocaleString()}</div>
                                    </div>
                                </div>
                                <ProgressBar 
                                    value={arch.avg_persuadability_score} 
                                    colorClass="bg-gradient-to-r from-purple-500 to-pink-500"
                                    label="Persuadability"
                                />
                            </div>
                        ))}
                        {strategicSegments.battleground.length === 0 && (
                            <div className="text-center py-4 text-sm text-slate-500">
                                No major swing segments identified.
                            </div>
                        )}
                    </div>
                </Panel>
            </div>
        </div>
    );
};

// --- SUB-COMPONENTS (Detail Views - Unchanged) ---
const ArchetypeKPICard = ({ archetype, wardAppearances, isSelected, onClick }) => {
  const turnoutLevel = archetype.avg_turnout_probability > 0.8 ? 'HIGH' : 
                      archetype.avg_turnout_probability > 0.6 ? 'MEDIUM' : 'LOW';
  const topIssues = Object.entries(archetype.issue_priorities || {})
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3);
  const topChannels = Object.entries(archetype.channel_receptivity || {})
    .sort(([,a], [,b]) => b - a)
    .slice(0, 2);
  const formatName = (name) => name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-panel rounded-lg border h-full cursor-pointer transition-all duration-300 hover:border-slate-500 ${
        isSelected ? 'border-sky-500 ring-1 ring-sky-500/50 bg-slate-800/80' : 'border-slate-700 hover:bg-slate-800/60'
      }`}
      onClick={onClick}
    >
      <div className="p-6 flex flex-col h-full">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
            <Users size={20} className="text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-lg leading-tight truncate">{archetype.name}</h3>
            <p className="text-slate-400 text-sm leading-snug truncate">{archetype.main_segment}</p>
            <span className="text-xs text-slate-500 mt-1 block">
              Across {wardAppearances} Wards
            </span>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-4">
          <ThreatLevelBadge level={turnoutLevel} type="TURNOUT" />
        </div>

        <div className="grid grid-cols-3 gap-4 text-center mb-4 border-t border-slate-700/50 pt-4">
          <div>
            <div className="text-2xl font-bold text-white">
                {archetype.estimated_population > 1000 ? `${(archetype.estimated_population / 1000).toFixed(1)}k` : archetype.estimated_population}
            </div>
            <div className="text-xs text-slate-400">People</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-sky-400">{topIssues.length}</div>
            <div className="text-xs text-slate-400">Key Issues</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-400">{topChannels.length}</div>
            <div className="text-xs text-slate-400">Top Channels</div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Top Issues</div>
          <div className="flex flex-wrap gap-1">
            {topIssues.length > 0 ? topIssues.map(([issue]) => (
              <span key={issue} className="text-xs px-2 py-1 bg-blue-500/10 text-blue-400 rounded border border-blue-500/10">
                {formatName(issue)}
              </span>
            )) : <span className="text-xs text-slate-500 italic">No priority issues data.</span>}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const NarrativeCard = ({ archetype, theme, index }) => {
    const generatePersonalizedNarrative = (archetype, theme) => {
      const isEstablishedLiberal = archetype.main_segment === 'Established Liberals';
      const isIncrementalist = archetype.main_segment === 'Incrementalist Left';
      
      switch (theme.title) {
        case "The challenges we face":
          if (isEstablishedLiberal) {
            return {
              headline: "Our institutions are failing the people who built them",
              message: "After decades of contributing to our communities and supporting our public services, you're watching them crumble. The NHS you helped build through your taxes can't deliver when your family needs it.",
              call_to_action: "Your experience and voice are needed to restore competent, principled government."
            };
          } else if (isIncrementalist) {
            return {
              headline: "Practical solutions are being ignored while families suffer",
              message: "You know what works in your community, but instead of building on proven approaches, we're getting political point-scoring while working families bear the cost.",
              call_to_action: "Let's focus on what actually works, not what sounds good in Westminster."
            };
          }
          break;
          
        case "A brighter future":
          if (isEstablishedLiberal) {
            return {
              headline: "Britain's best days can still be ahead of us",
              message: "This country has always thrived when we combine innovation with stability, ambition with responsibility. We need leadership that builds on these strengths.",
              call_to_action: "Together, we can restore Britain as a confident, outward-looking nation."
            };
          } else if (isIncrementalist) {
            return {
              headline: "Step by step, we can build the society we want to see",
              message: "Real change happens when communities come together to solve problems. Progress comes from people who care enough to act.",
              call_to_action: "Every voice matters, every contribution counts, every community deserves champions."
            };
          }
          break;
          
        case "The Liberal Democrat response":
          if (isEstablishedLiberal) {
            return {
              headline: "Responsible opposition, credible alternative",
              message: "As the biggest liberal party in Parliament in over 100 years, we're offering serious, evidence-based policies Britain needs.",
              call_to_action: "Support the party that combines principles with pragmatism."
            };
          } else if (isIncrementalist) {
            return {
              headline: "Practical policies that put people first",
              message: "While others offer empty promises, we're developing detailed plans that will actually work, consulting widely and gathering evidence.",
              call_to_action: "Join us in building policies from the ground up - your experience matters."
            };
          }
          break;
        default: break;
      }
      
      return {
        headline: theme.core_message,
        message: theme.key_points.slice(0, 2).join('. ') + '.',
        call_to_action: "Join us in making a difference."
      };
    };
  
    const narrative = generatePersonalizedNarrative(archetype, theme);
    
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.1 }}
        className="bg-slate-700/30 border border-slate-600 rounded-lg p-4 space-y-3"
      >
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-white">{theme.title}</h4>
          <MessageSquare size={16} className="text-blue-400" />
        </div>
        <div className="space-y-3">
          <div>
            <h5 className="font-medium text-blue-400 text-sm mb-2">{narrative.headline}</h5>
            <p className="text-slate-300 text-xs leading-relaxed">{narrative.message}</p>
          </div>
          <div className="border-t border-slate-600 pt-3">
            <p className="text-sky-400 text-xs font-medium">{narrative.call_to_action}</p>
          </div>
        </div>
      </motion.div>
    );
  };
  
  const CommunicationChannelCard = ({ channel, effectiveness, index }) => {
    const getChannelIcon = (channel) => {
      switch (channel) {
        case 'email': return <FileText size={16} />;
        case 'postal_leaflet': return <Home size={16} />;
        case 'door_to_door': return <Users size={16} />;
        case 'digital_ads': return <Eye size={16} />;
        case 'phone_banking': return <MessageSquare size={16} />;
        case 'local_media': return <TrendingUp size={16} />;
        default: return <Target size={16} />;
      }
    };
    const getChannelName = (channel) => {
      const names = {
        'email': 'Email Campaigns',
        'postal_leaflet': 'Direct Mail', 
        'door_to_door': 'Door Knocking',
        'digital_ads': 'Digital Advertising',
        'phone_banking': 'Phone Outreach',
        'local_media': 'Local Media'
      };
      return names[channel] || channel.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };
    const getEffectivenessLevel = (score) => {
      if (score >= 0.8) return { level: 'Highly Effective', color: 'text-green-400' };
      if (score >= 0.6) return { level: 'Effective', color: 'text-blue-400' };
      if (score >= 0.4) return { level: 'Moderately Effective', color: 'text-yellow-400' };
      return { level: 'Limited Effectiveness', color: 'text-red-400' };
    };
  
    const { level, color } = getEffectivenessLevel(effectiveness);
  
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.1 }}
        className="bg-slate-700/30 border border-slate-600 rounded-lg p-4 space-y-3"
      >
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-white flex items-center">
            <span className="mr-2">{getChannelIcon(channel)}</span>
            {getChannelName(channel)}
          </h4>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Effectiveness</span>
            <span className={`text-sm font-medium ${color}`}>{level}</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-slate-700 to-green-500 h-2 rounded-full"
              style={{ width: `${effectiveness * 100}%` }}
            />
          </div>
        </div>
      </motion.div>
    );
  };
  
  const IssuePriorityCard = ({ issue, importance, index }) => {
    const getIssueIcon = (issue) => {
      switch (issue) {
        case 'economy': return <TrendingUp size={16} />;
        case 'healthcare': return <Heart size={16} />;
        case 'education': return <Book size={16} />;
        case 'local_services': return <MapPin size={16} />;
        case 'housing': return <Home size={16} />;
        case 'europe': return <Shield size={16} />;
        case 'trade': return <Briefcase size={16} />;
        case 'cost_of_living': return <Zap size={16} />;
        default: return <Target size={16} />;
      }
    };
    const getIssueName = (issue) => {
      const names = {
        'economy': 'Economic Policy',
        'healthcare': 'Healthcare & NHS',
        'education': 'Education',
        'local_services': 'Local Services',
        'housing': 'Housing',
        'europe': 'Europe & Trade',
        'trade': 'International Trade',
        'cost_of_living': 'Cost of Living'
      };
      return names[issue] || issue.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };
    const getImportanceLevel = (score) => {
      if (score >= 0.75) return { level: 'Top Priority', color: 'text-red-400' };
      if (score >= 0.65) return { level: 'High Priority', color: 'text-orange-400' };
      if (score >= 0.55) return { level: 'Important', color: 'text-yellow-400' };
      return { level: 'Moderate Interest', color: 'text-slate-400' };
    };
  
    const { level, color } = getImportanceLevel(importance);
  
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.1 }}
        className="bg-slate-700/30 border border-slate-600 rounded-lg p-4 space-y-3"
      >
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-white flex items-center">
            <span className="mr-2">{getIssueIcon(issue)}</span>
            {getIssueName(issue)}
          </h4>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Priority Level</span>
            <span className={`text-sm font-medium ${color}`}>{level}</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 h-2 rounded-full"
              style={{ width: `${importance * 100}%` }}
            />
          </div>
        </div>
      </motion.div>
    );
  };
  
  const PsychographicsPanel = ({ psychographics }) => {
    const data = psychographics || {};
  
    const synthesizeOcean = (profile) => {
      if (!profile) return { title: "Personality (OCEAN)", items: [] };
      const traits = [
        { key: 'O', name: 'Openness', high: 'Highly open, curious, and creative.', low: 'Prefers routine and is more conventional.' },
        { key: 'C', name: 'Conscientiousness', high: 'Highly conscientious, organized, and reliable.', low: 'More spontaneous, flexible, less organized.' },
        { key: 'E', name: 'Extraversion', high: 'Highly extraverted, sociable, and energetic.', low: 'More introverted and reserved.' },
        { key: 'A', name: 'Agreeableness', high: 'Highly agreeable, compassionate, and cooperative.', low: 'More analytical, competitive, and challenging.' },
        { key: 'N', name: 'Neuroticism', high: 'More sensitive to stress.', low: 'Emotionally stable and resilient.' },
      ];
      
      const items = traits.map(trait => {
        const score = profile[trait.key];
        if (score === undefined) return null;
        if (score > 0.65) return { name: trait.name, desc: trait.high, color: 'text-green-400' };
        if (score < 0.35) return { name: trait.name, desc: trait.low, color: 'text-yellow-400' };
        return null;
      }).filter(Boolean);
      
      if (items.length === 0) {
        items.push({ name: "Profile", desc: "This group shows a balanced or moderate personality profile.", color: 'text-slate-400' });
      }
      
      return { title: "Key Personality Traits", items };
    };
  
    const synthesizeMft = (profile) => {
      if (!profile) return { title: "Moral Framework (MFT)", items: [] };
      const individualizing = (profile.care || 0) + (profile.fairness || 0);
      const binding = (profile.loyalty || 0) + (profile.authority || 0) + (profile.sanctity || 0);
      let summary;
      if (individualizing > binding * 1.2) {
        summary = "Primarily driven by individual-focused morals (Care, Fairness).";
      } else if (binding > individualizing * 1.2) {
        summary = "Primarily driven by group-focused morals (Loyalty, Authority).";
      } else {
        summary = "Shows a balance between individual and group moral foundations.";
      }
      return { title: "Moral Framework", items: [{ name: "Core Driver", desc: summary, color: 'text-blue-400' }] };
    };
    
    const synthesizeMedia = (profile) => {
      if (!profile) return { title: "Media Consumption", items: [] };
      const sortedMedia = Object.entries(profile)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3);
      if (sortedMedia.length === 0) return { title: "Media Consumption", items: [] };
      const items = sortedMedia.map(([media, score]) => {
        const name = media.replace(/_/g, ' ');
        let desc;
        if(score > 0.7) desc = `High engagement with ${name}.`;
        else if (score > 0.5) desc = `Regular engagement with ${name}.`;
        else desc = `Some engagement with ${name}.`;
        return { name: "Top Channel", desc, color: 'text-teal-400' };
      });
      return { title: "Media Consumption", items };
    };
    
    const synthesizeVals = (profile) => {
      if (!profile || !profile.primary_type) return { title: "Lifestyle (VALS)", items: [] };
      const descriptions = {
        'Innovators': 'Sophisticated, high self-esteem. Image is important.',
        'Thinkers': 'Motivated by ideals. Mature, reflective, and well-informed.',
        'Achievers': 'Goal-oriented, value stability. Family and career focused.',
        'Experiencers': 'Motivated by self-expression. Young, enthusiastic, seek variety.',
        'Believers': 'Motivated by ideals. Conservative, conventional, community-focused.',
        'Strivers': 'Trendy and fun-loving. Motivated by achievement, concerned about opinions.',
        'Makers': 'Motivated by self-expression. Practical, self-sufficient, value work.',
        'Survivors': 'Narrowly focused lives. Cautious, value safety and security.',
      };
      const desc = descriptions[profile.primary_type] || "No description available.";
      return { 
        title: "Lifestyle (VALS)", 
        items: [{ name: profile.primary_type, desc, color: 'text-purple-400' }]
      };
    };
  
    const oceanSummary = synthesizeOcean(data.ocean_profile);
    const mftSummary = synthesizeMft(data.mft_profile);
    const mediaSummary = synthesizeMedia(data.media_consumption);
    const valsSummary = synthesizeVals(data.vals_profile);
    
    const allSummaries = [
      { icon: <Brain size={16} />, ...oceanSummary },
      { icon: <Shield size={16} />, ...mftSummary },
      { icon: <Eye size={16} />, ...mediaSummary },
      { icon: <LifeBuoy size={16} />, ...valsSummary },
    ].filter(s => s.items.length > 0);
  
    if (allSummaries.length === 0) {
      return (
        <Panel title="Psychological & Lifestyle Profile" icon={<Sparkles size={20} className="text-purple-400" />}>
          <p className="text-slate-400 italic">No psychographic summary data is available for this archetype.</p>
        </Panel>
      );
    }
  
    return (
      <Panel title="Psychological & Lifestyle Profile" icon={<Sparkles size={20} className="text-purple-400" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {allSummaries.map((summary, index) => (
            <div key={index} className="bg-slate-700/30 border border-slate-600 rounded-lg p-4">
              <h4 className="flex items-center font-semibold text-white mb-3">
                <span className="mr-2">{summary.icon}</span>
                {summary.title}
              </h4>
              <ul className="space-y-2">
                {summary.items.map((item, idx) => (
                  <li key={idx} className="text-sm">
                    <span className={`font-medium ${item.color} capitalize`}>{item.name.replace(/_/g, ' ')}:</span>
                    <span className="text-slate-300 ml-1">{item.desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Panel>
    );
  };

  const SynthesizedDemographicsPanel = ({ archetype, totalWards }) => {
    const getLifeStage = (age) => {
      if (age < 30) return { stage: "Younger Voter", color: "text-blue-400" };
      if (age < 45) return { stage: "Established Professional", color: "text-green-400" };
      if (age < 60) return { stage: "Mid-Career Homeowner", color: "text-yellow-400" };
      return { stage: "Senior/Retired", color: "text-orange-400" };
    };
    const getAffluence = (income, houseValue) => {
      if (income > 50000 || houseValue > 400000) return { level: "High Affluence", color: "text-green-400" };
      if (income > 35000 || houseValue > 250000) return { level: "Moderate Affluence", color: "text-yellow-400" };
      return { level: "Below Average", color: "text-orange-400" };
    };
    const getConcentration = (wardAppearances, totalWards) => {
      if (totalWards === 0) return { level: "N/A", pct: 0 };
      const pct = (wardAppearances / totalWards) * 100;
      let level;
      if (pct > 75) level = "Highly Clustered";
      else if (pct > 40) level = "Geographically Common";
      else if (pct > 10) level = "Sparsely Distributed";
      else level = "Rare / Highly Localized";
      return { level, pct, color: "text-purple-400" };
    };
    
    const lifeStage = getLifeStage(archetype.avg_age);
    const affluence = getAffluence(archetype.avg_income, archetype.avg_house_value);
    const concentration = getConcentration(archetype.ward_appearances, totalWards);
    
    const stats = [
      { label: "Average Age", value: archetype.avg_age.toFixed(0), icon: <UserIcon size={16} /> },
      { label: "Average Income", value: `£${(archetype.avg_income / 1000).toFixed(0)}k`, icon: <Briefcase size={16} /> },
      { label: "Avg. House Value", value: `£${(archetype.avg_house_value / 1000).toFixed(0)}k`, icon: <Home size={16} /> },
      { label: "Avg. Turnout", value: `${(archetype.avg_turnout_probability * 100).toFixed(0)}%`, icon: <Percent size={16} /> }
    ];
    
    return (
      <Panel title="Average Demographics" icon={<PieChart size={20} className="text-blue-400" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Life Stage</h4>
              <p className={`text-lg font-bold ${lifeStage.color}`}>{lifeStage.stage}</p>
              <p className="text-xs text-slate-400">(Based on avg. age of {archetype.avg_age.toFixed(0)})</p>
            </div>
            <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Affluence Profile</h4>
              <p className={`text-lg font-bold ${affluence.color}`}>{affluence.level}</p>
              <p className="text-xs text-slate-400">(Based on income & house value)</p>
            </div>
            <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Geographic Spread</h4>
              <p className={`text-lg font-bold ${concentration.color}`}>{concentration.level}</p>
              <p className="text-xs text-slate-400">(Appears in {concentration.pct.toFixed(0)}% of campaign wards)</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 h-full">
            {stats.map(stat => (
              <div key={stat.label} className="bg-slate-700/30 rounded-lg p-4 border border-slate-700/50 flex flex-col items-center justify-center h-full">
                <div className="text-slate-400">{stat.icon}</div>
                <div className="text-2xl font-bold text-white mt-2">
                  {stat.value}
                </div>
                <div className="text-xs text-slate-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Panel>
    );
  };
  
  const InfoItem = ({ label, children, className = "" }) => (
    <div className={`py-3 border-b border-slate-700/50 last:border-b-0 ${className}`}>
      <dt className="text-sm font-medium text-slate-400 uppercase tracking-wide">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-slate-200 leading-relaxed">
        {children}
      </dd>
    </div>
  );
  
  const renderBadges = (items) => (
    <div className="flex flex-wrap gap-2">
      {items.map(item => (
        <span key={item} className="text-xs px-2 py-1 bg-sky-500/10 text-sky-400 rounded-md font-medium">
          {item}
        </span>
      ))}
    </div>
  );
  
  const ArchetypePoliticalBioPanel = ({ bioData }) => {
    const [isBioOpen, setIsBioOpen] = useState(true);
  
    if (!bioData) return null; 
  
    return (
      <Panel 
        title="Political Biography" 
        icon={<FileText size={20} className="text-sky-400" />}
        controls={
          <button 
            onClick={() => setIsBioOpen(!isBioOpen)}
            className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
            aria-label={isBioOpen ? "Collapse section" : "Expand section"}
          >
            <motion.div
              animate={{ rotate: isBioOpen ? 0 : -180 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={20} />
            </motion.div>
          </button>
        }
      >
        <AnimatePresence>
          {isBioOpen && (
            <motion.div
              key="bio-content"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-0 bg-slate-700/30 border border-slate-600 rounded-lg p-4">
                  <InfoItem label="Political Leaning">{bioData.political_leaning}</InfoItem>
                  <InfoItem label="Probable Age Range">{bioData.probable_age_range}</InfoItem>
                  <InfoItem label="Education Skew">{bioData.education_skew}</InfoItem>
                  <InfoItem label="Income Skew">{bioData.income_skew}</InfoItem>
                  <InfoItem label="Region Notes">{bioData.region_notes}</InfoItem>
                   <InfoItem label="Turnout Reliability (Contextual)">{bioData.turnout_reliability}</InfoItem>
                </div>
                <div className="space-y-0 md:col-span-2 bg-slate-700/30 border border-slate-600 rounded-lg p-4">
                  <InfoItem label="Disposition">{bioData.disposition}</InfoItem>
                  <InfoItem label="Key Concerns">{renderBadges(bioData.key_concerns)}</InfoItem>
                  <InfoItem label="Primary Mobilisers">{renderBadges(bioData.primary_mobilisers)}</InfoItem>
                  <InfoItem label="Tactical Voting Susceptibility">{bioData.tactical_voting_susceptibility}</InfoItem>
                </div>
                <div className="md:col-span-3 bg-slate-700/30 border border-slate-600 rounded-lg p-4">
                  <InfoItem label="Voting Propensity Summary">{bioData.voting_propensity_summary}</InfoItem>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Panel>
    );
  };


// --- MAIN VIEWER COMPONENT ---

export default function VoterArchetypeViewer({ campaignData }) {
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = globalStyles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Initialize as null (Dashboard view)
  const [selectedArchetype, setSelectedArchetype] = useState(null);
  
  const allArchetypes = useMemo(() => {
    // ROBUST DATA ACCESS: Check archetype_summary OR archetypeData
    const archetypes = campaignData?.archetype_summary || campaignData?.archetypeData || [];
    return archetypes.map((archetype, index) => ({
      ...archetype,
      id: archetype.category || `archetype-${index}` 
    }));
  }, [campaignData]);
  
  // ROBUST METRICS ACCESS
  const totalWardsInSummary = campaignData?.total_wards_in_summary || campaignData?.wardsProcessed || 0;

  const handleArchetypeSelect = (archetype) => {
    setSelectedArchetype(archetype);
  };

  const clearSelection = () => {
    setSelectedArchetype(null);
  }

  const selectedArchetypeStaticData = useMemo(() => {
    if (!selectedArchetype || !selectedArchetype.category) return null;
    const key = selectedArchetype.category; 
    return archetypeSummaryData[key] || null; 
  }, [selectedArchetype]);

  return (
    <div className="p-4 md:p-8 space-y-8 min-h-screen">
      
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Voter Intelligence</h1>
          <p className="text-slate-400 flex items-center text-sm">
            <Clock size={14} className="mr-2" />
            Last updated: {campaignData?.generation_timestamp 
              ? new Date(campaignData.generation_timestamp).toLocaleString('en-GB', {
                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                })
              : "Now"}
          </p>
        </div>
        {selectedArchetype && (
             <button 
                onClick={clearSelection}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm text-white transition-colors"
             >
                <TrendingUp size={16} />
                <span>Return to Dashboard</span>
             </button>
        )}
      </div>

      {/* TOP CAROUSEL - ALWAYS VISIBLE */}
      <div className="relative">
        <div className="flex overflow-x-auto space-x-6 gap-0 pb-4 scrollbar-hide px-1">
          {allArchetypes.map(archetype => (
            <div key={archetype.id} className="w-80 md:w-96 flex-shrink-0">
              <ArchetypeKPICard
                archetype={archetype}
                wardAppearances={archetype.ward_appearances}
                isSelected={selectedArchetype?.id === archetype.id}
                onClick={() => handleArchetypeSelect(archetype)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <AnimatePresence mode="wait">
        
        {/* --- STATE 1: DASHBOARD (DEFAULT) --- */}
        {!selectedArchetype ? (
            <motion.div
                key="dashboard"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
            >
                <CampaignDashboard campaignData={campaignData} />
            </motion.div>
        ) : (
            
        /* --- STATE 2: DETAILED ARCHETYPE VIEW --- */
          <motion.div
            key="details"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="border-t-2 border-slate-700/50 pt-8 space-y-8"
          >
             {/* Header Detail Panel */}
            <Panel>
              <div className="flex flex-col md:flex-row items-start justify-between gap-6">
                <div className="flex items-start space-x-4 md:space-x-6 w-full">
                  <div className="w-24 h-32 md:w-32 md:h-40 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg relative overflow-hidden group">
                     <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <Users size={32} className="text-white relative z-10" />
                  </div>
                  <div className="space-y-3 flex-grow min-w-0">
                    <div>
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl md:text-2xl font-bold text-white">{selectedArchetype.name}</h2>
                        <button onClick={clearSelection} className="md:hidden text-slate-400 hover:text-white">
                            <X size={24} />
                        </button>
                      </div>
                      <p className="text-base md:text-lg text-slate-300">{selectedArchetype.main_segment}</p>
                      <p className="text-sm text-sky-400">
                        Appears in {selectedArchetype.ward_appearances} wards
                      </p>
                    </div>
                    
                    <div className="mt-4 border-t border-slate-700/50 pt-4">
                      <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Bio</h4>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        {selectedArchetypeStaticData 
                          ? selectedArchetypeStaticData.political_biography.paragraph_summary
                          : <span className="italic text-slate-500">Static summary bio not found.</span> 
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-left md:text-right space-y-2 flex-shrink-0 w-full md:w-auto md:ml-6">
                  <div className="text-3xl font-bold text-sky-400">
                    {selectedArchetype.estimated_population.toLocaleString()}
                  </div>
                  <div className="text-sm text-slate-400">Total People</div>
                  <div className="text-xs text-slate-300">
                    {(selectedArchetype.percentage_of_total_campaign_pop || 0).toFixed(1)}% of campaign
                  </div>
                </div>
              </div>
            </Panel>

            <ArchetypePoliticalBioPanel bioData={selectedArchetypeStaticData?.political_biography} />

            <Panel title="Issue Priorities" icon={<Target size={20} className="text-red-400" />}>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {Object.entries(selectedArchetype.issue_priorities || {})
                  .sort(([,a], [,b]) => b - a)
                  .map(([issue, importance], index) => (
                    <IssuePriorityCard key={issue} issue={issue} importance={importance} index={index} />
                  ))}
              </div>
            </Panel>

            <Panel title="Communication Channels" icon={<MessageSquare size={20} className="text-green-400" />}>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {Object.entries(selectedArchetype.channel_receptivity || {})
                  .sort(([,a], [,b]) => b - a)
                  .map(([channel, effectiveness], index) => (
                    <CommunicationChannelCard key={channel} channel={channel} effectiveness={effectiveness} index={index} />
                  ))}
              </div>
            </Panel>
            
            <PsychographicsPanel psychographics={selectedArchetype.psychographics} />

            <Panel title="Tailored Narratives" icon={<Brain size={20} className="text-purple-400" />}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {Object.values(narrativeThemes).map((theme, index) => (
                  <NarrativeCard key={index} archetype={selectedArchetype} theme={theme} index={index} />
                ))}
              </div>
            </Panel>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <SynthesizedDemographicsPanel archetype={selectedArchetype} totalWards={totalWardsInSummary} />
              <Panel title="Engagement Profile" icon={<Activity size={20} className="text-yellow-400" />}>
                <div className="space-y-4">
                  <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Conversion Potential</h4>
                    <p className={`text-lg font-bold text-teal-400`}>
                      {
                        (() => {
                          const p = selectedArchetype.avg_persuadability_score || 0;
                          const t = selectedArchetype.avg_turnout_probability || 0;
                          if (p > 0.6 && t > 0.6) return "Prime Target";
                          if (p > 0.6 && t <= 0.6) return "GOTV Challenge";
                          if (p <= 0.6 && t > 0.6) return "Entrenched Voter";
                          return "Low Priority";
                        })()
                      }
                    </p>
                    <p className="text-xs text-slate-400">(Based on Persuadability & Turnout)</p>
                  </div>
                  <div className="bg-slate-700/20 rounded p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-400">Persuadability</span>
                      <span className="text-sm font-medium text-white">
                        {selectedArchetype.avg_persuadability_score > 0.7 ? 'High' : 
                         selectedArchetype.avg_persuadability_score > 0.5 ? 'Medium' : 'Low'}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-slate-700 to-purple-500 h-2 rounded-full"
                        style={{ width: `${(selectedArchetype.avg_persuadability_score || 0) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="bg-slate-700/20 rounded p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-400">Turnout Reliability</span>
                      <span className="text-sm font-medium text-white">
                        {selectedArchetype.avg_turnout_probability > 0.8 ? 'Reliable' : 
                         selectedArchetype.avg_turnout_probability > 0.6 ? 'Likely' : 'Variable'}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-slate-700 to-green-500 h-2 rounded-full"
                        style={{ width: `${(selectedArchetype.avg_turnout_probability || 0) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </Panel>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}